import { useEffect, useLayoutEffect, useState } from 'react';
import { SPLASH_PHASE, getSplashPhase, useSplashPhase } from './splashHandoff';

/**
 * A splash raised this soon after a screen mounts — and before any splash was
 * seen leaving under it — still counts as that screen's FIRST cover. The
 * dashboard's first-load hold registers its signal in an effect, a commit
 * AFTER the home (and its welcome gate) mounted; after the forced 2FA
 * enrollment gate, nothing else covers that commit, so the gate's first
 * render saw no splash and lost the handoff (KAN-562 follow-up).
 */
export const LATE_COVER_WINDOW_MS = 1000;

/**
 * useFirstSplashCover() — true while the FIRST splash cover of this mount is
 * up: one already up at mount, or one that comes up within
 * LATE_COVER_WINDOW_MS of mount before any leave was seen. False for good the
 * moment that cover starts to leave (a later splash blip in the session never
 * re-opens the handoff), or if no cover came in the window.
 */
export function useFirstSplashCover() {
  const phase = useSplashPhase();
  // 'waiting' (no cover yet) → 'covered' → 'done'; 'done' is final.
  const [state, setState] = useState(() => (getSplashPhase() === SPLASH_PHASE.COVERED ? 'covered' : 'waiting'));

  useLayoutEffect(() => {
    if (state === 'waiting') {
      if (phase === SPLASH_PHASE.COVERED) setState('covered');
      else if (phase === SPLASH_PHASE.LEAVING) setState('done');
    } else if (state === 'covered' && phase !== SPLASH_PHASE.COVERED) {
      setState('done');
    }
  }, [state, phase]);

  useEffect(() => {
    if (state !== 'waiting') return undefined;
    const t = setTimeout(() => setState((s) => (s === 'waiting' ? 'done' : s)), LATE_COVER_WINDOW_MS);
    return () => clearTimeout(t);
  }, [state]);

  // Read in render too, so it is never true for even one render past the cover leaving.
  return state === 'covered' && phase === SPLASH_PHASE.COVERED;
}

export default useFirstSplashCover;
